// src/components/about/TeamSection.js
import React from 'react';
import './TeamSection.css'; // Importe son propre CSS
import pb from '../../lib/pocketbase';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

function fileUrl(record, fileField, thumb) {
  try {
    if (!record || !record[fileField]) return '';
    return pb.files.getUrl(record, record[fileField], thumb ? { thumb } : undefined);
  } catch {
    return '';
  }
}

function isHttpUrl(url) {
  return /^https?:\/\//i.test(String(url || '').trim());
}

function normalizeLink(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.includes('@') && !raw.startsWith('mailto:') && !isHttpUrl(raw)) return `mailto:${raw}`;
  if (!isHttpUrl(raw) && !raw.startsWith('mailto:')) return `https://${raw}`;
  return raw;
}

function TeamSection() {
  const fallbackTeamMembers = [
    {
      id: 1,
      name: "Léon",
      role: "Développeur Full-Stack & Fondateur",
      // Assure-toi que ce chemin est correct ou utilise un placeholder plus générique si l'image n'existe pas
      imageUrl: "/images/team/pi1.jpg",
      tagline: '',
      bio_short: '',
      bio: '',
      linkedin_url: '',
      website_url: '',
      email: '',
    },
    {
      id: 2,
      name: "Thibault", // Nom changé pour l'exemple
      role: "Designer UX/UI & Chef de Projet",
      imageUrl: "/images/team/tibo.jpeg",
      tagline: '',
      bio_short: '',
      bio: '',
      linkedin_url: '',
      website_url: '',
      email: '',
    },
    {
      id: 3, // Ajout d'un troisième membre pour l'exemple
      name: "Teva",
      role: "Spécialiste Marketing Digital & SEO",
      imageUrl: "/images/team/teva.jpeg", // Crée cette image placeholder
      tagline: '',
      bio_short: '',
      bio: '',
      linkedin_url: '',
      website_url: '',
      email: '',
    }
    // Ajoute d'autres membres
  ];

  const [teamMembers, setTeamMembers] = React.useState(fallbackTeamMembers);
  const [expandedMap, setExpandedMap] = React.useState({});

  const toggleBio = (clickedId) => {
    // Determine the target state based on whether the clicked card is currently closed (will open) or open (will close)
    const isCurrentlyOpen = !!expandedMap[clickedId];
    const targetState = !isCurrentlyOpen;

    // Create a new map where ALL member IDs are set to the target state
    const newExpandedMap = {};
    teamMembers.forEach(member => {
      newExpandedMap[member.id] = targetState;
    });
    setExpandedMap(newExpandedMap);
  };

  React.useEffect(() => {
    let canceled = false;

    const load = async () => {
      try {
        const res = await pb.collection('site_team_members').getList(1, 200, { sort: 'sort_order, -created', filter: 'is_active = true' });
        const items = (res?.items ?? []).map((it) => ({
          id: it.id,
          name: it.name || '',
          role: it.role || '',
          tagline: it.tagline || '',
          bio_short: it.bio_short || '',
          bio: it.bio || '',
          linkedin_url: it.linkedin_url || '',
          website_url: it.website_url || '',
          email: it.email || '',
          imageUrl: fileUrl(it, 'photo', '400x400') || '',
        })).filter((it) => it.name);

        if (!canceled && items.length) setTeamMembers(items);
      } catch {
        // keep fallback
      }
    };

    load();
    return () => { canceled = true; };
  }, []);



  const [swiperInstance, setSwiperInstance] = React.useState(null);

  React.useEffect(() => {
    if (swiperInstance) {
      // Force Swiper to re-calculate height after a short delay to allow CSS transition to start/finish
      // We trigger it multiple times to ensure the animation is followed
      const timeouts = [50, 200, 450].map(delay =>
        setTimeout(() => {
          swiperInstance.update();
          swiperInstance.updateAutoHeight();
        }, delay)
      );
      return () => timeouts.forEach(clearTimeout);
    }
  }, [expandedMap, swiperInstance]);

  return (
    // On utilise la classe "about-section" de AboutPage.css pour le style commun
    // et "team-section-container" si on a des styles spécifiques dans TeamSection.css
    <section className="about-section team-section-container">
      <h2>Notre Équipe</h2>
      <p className="section-intro-team"> {/* Classe pour le paragraphe d'intro */}
        Derrière chaque projet réussi se trouve une équipe passionnée et compétente.
        Voici ceux qui font la force d'INFO COLAB :
      </p>
      <Swiper
        modules={[Navigation, Pagination, A11y, Autoplay]}
        spaceBetween={40}
        slidesPerView={1}
        loop
        autoplay={{
          delay: 3200,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        navigation
        pagination={{ clickable: true }}
        breakpoints={{
          650: { slidesPerView: 1.2 },
          900: { slidesPerView: 2 },
        }}
        className="team-swiper"
        autoHeight={true}
        observer={true}
        observeParents={true}
        observeSlideChildren={true}
        onSwiper={setSwiperInstance}
      >
        {teamMembers.map(member => {
          const isExpanded = !!expandedMap[member.id];
          return (
            <SwiperSlide key={member.id} className="team-slide">
              <div className={`team-member-card ${isExpanded ? 'expanded' : ''}`}>
                <img src={member.imageUrl || '/images/team/default-avatar.png'} alt={member.name} />
                <h3>{member.name}</h3>
                <p className="role">{member.role}</p>
                {member.tagline ? <p className="tagline">{member.tagline}</p> : null}

                {/* Toujours afficher bio_short si elle existe */}
                {member.bio_short ? <p className="bio-short">{member.bio_short}</p> : null}

                {/* Contenu extensible */}
                <div className={`team-bio-expandable ${isExpanded ? 'open' : ''}`}>
                  <p className="team-bio-text">{member.bio}</p>
                  {/* Liens sociaux dans la partie dépliée */}
                  {(member.linkedin_url || member.website_url || member.email) && (
                    <div className="team-bio-links">
                      {member.linkedin_url && <a href={normalizeLink(member.linkedin_url)} target="_blank" rel="noreferrer">LinkedIn</a>}
                      {member.website_url && <a href={normalizeLink(member.website_url)} target="_blank" rel="noreferrer">Site</a>}
                      {member.email && <a href={normalizeLink(member.email)} rel="noreferrer">Email</a>}
                    </div>
                  )}
                </div>

                {/* Bouton Toggle */}
                {(member.bio || member.linkedin_url || member.website_url || member.email) ? (
                  <button type="button" className="team-bio-btn" onClick={() => toggleBio(member.id)}>
                    {isExpanded ? 'Réduire' : 'En savoir plus'}
                  </button>
                ) : null}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
}

export default TeamSection;